import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import GridBackdrop from "@/components/GridBackdrop";
import { Button } from "@/components/ui/button";
import { Check, Minus, Zap, Shield, RefreshCw } from "lucide-react";
import { Link } from "@/lib/router-compat";
import type { ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const benefits = [
  {
    icon: Zap,
    title: "Maintain Performance",
    description: "We keep your site fast in a world of constantly updating browsers, devices, plugins, and hosting environments.",
  },
  {
    icon: Shield,
    title: "Stay Secure",
    description: "Security threats evolve quickly. We monitor and update your site to minimize risk and keep it protected.",
  },
  {
    icon: RefreshCw,
    title: "Keep It Fresh",
    description: "We help you swap out the static parts that stale: images, content changes, and any tech updates with priority support.",
  },
];

const solutions = [
  "Full website hosting across a globally distributed platform with great uptime and zero configuration required.",
  "SSL certificates, staging environments, and DNS configuration at no extra cost.",
  "Proactive monitoring and alerting so we know when something goes wrong before you do.",
  "Real-time analytics, error tracking, and performance monitoring built-in to every plan included.",
  "Expert availability with our Slack-like chat interface or email-based communication.",
  "Work directly with your builder—no middlemen, no ticket systems, just quick and reliable support.",
];

const plans = [
  {
    name: "Essential",
    price: "$197",
    period: "/month",
    annual: "or $1,970/Year Save $394",
    description: "Perfect for small sites with minimal upkeep needs and low traffic",
    features: [
      { name: "Website Hosting", included: true },
      { name: "Managed Domain Renewal", included: true },
      { name: "DNS Management", included: true },
      { name: "SSL Certificate & Security", included: true },
      { name: "Weekly Backups", included: true },
      { name: "Email Setup Guidance", included: true },
      { name: "Content Updates", included: "1 hour / month" },
      { name: "Support Response Time", included: "48 hours" },
      { name: "Security Patches", included: true },
      { name: "Monthly Health Report", included: true },
    ],
    cta: "Get Started Plan",
    variant: "outline" as const,
  },
  {
    name: "Professional",
    price: "$347",
    period: "/month",
    annual: "or $3,470/Year Save $694",
    description: "Ideal for growing business sites with regular content updates",
    popular: true,
    features: [
      { name: "Everything in Essential", included: true },
      { name: "Content Updates", included: "2 hours / month" },
      { name: "Support Response Time", included: "24 hours" },
      { name: "Performance Optimization", included: "Monthly" },
      { name: "Uptime Monitoring", included: "Hourly" },
      { name: "Security Scanning & Fixes", included: true },
      { name: "Improvements & Fixes", included: "Quarterly" },
      { name: "Staging Environment", included: true },
    ],
    cta: "Get Professional Plan",
    variant: "default" as const,
  },
  {
    name: "Premium",
    price: "$597",
    period: "/month",
    annual: "or $5,970/Year Save $1,194",
    description: "For high-traffic sites and teams with robust or complex hosting/integrations",
    features: [
      { name: "Everything in Professional", included: true },
      { name: "Content Updates", included: "4 hours / month" },
      { name: "Support Response Time", included: "Same Day" },
      { name: "Performance Optimization", included: "Weekly" },
      { name: "Uptime Monitoring", included: "Real-time" },
      { name: "Improvements & Fixes", included: "Monthly" },
      { name: "Priority Support (Slack)", included: true },
      { name: "API Hosting & Databases", included: true },
      { name: "Dedicated Account Manager", included: true },
    ],
    cta: "Get Premium Plan",
    variant: "outline" as const,
  },
];

const faqs: { question: string; answer: ReactNode }[] = [
  {
    question: "Can I pause, downgrade, or cancel anytime?",
    answer: "Got a break, low season, or just need to scale back? No problem. You can pause, downgrade, or cancel anytime with a month's notice. We'll archive everything safely for when you're ready to ramp back up.",
  },
  {
    question: "How do maintenance plans work?",
    answer: "Your plan renews automatically each month (or year) using your chosen payment method. Cancel or adjust anytime. You'll get an invoice for each renewal, and if prices ever change, we notify you well in advance.",
  },
  {
    question: "What hosting platforms do you use?",
    answer: (
      <>
        We help you set up Vercel, Netlify, and business email through{" "}
        <Link to="/business-email" className="underline hover:text-foreground transition-colors">
          Google Workspace
        </Link>{" "}
        or{" "}
        <a
        href="https://www.microsoft.com/en-us/microsoft-365/business/business-email-address"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-foreground transition-colors"
        >
          Microsoft 365
        </a>
        , and manage your DNS records. All of which can be accessed and modified in the monthly or yearly plan subscriptions. We handle the setup, configuration, and ongoing management.
      </>
    ),
  },
  {
    question: "What if I need something not included in my plan?",
    answer: "No problem—just let us know. If the task falls outside your plan, we'll quote it separately or handle it at a fair hourly rate. No surprises, no pressure.",
  },
  {
    question: "What happens if something breaks?",
    answer: "Absolutely. You own your site and all its components. If you decide to leave, we provide a full export of your code and content with all assets. There's no lock-in and no hoops to jump through.",
  },
];

const Maintenance = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title="Website Maintenance Plans"
        description="All-inclusive website maintenance from $197/month. Hosting, updates, security, and support from our Orlando, FL team—serving clients worldwide."
        path="/services/maintenance"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <GridBackdrop />
          <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
                Keep Your Site Top-Notch
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                We build your site fast and right. Our all-inclusive maintenance ensures it stays that 
                way—fast, secure, and performing at peak levels.
              </p>
            </div>
          </div>
        </section>

        {/* Why Maintenance Matters */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">
              Why Maintenance Matters
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <benefit.icon className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* One-Stop Solution */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">One-Stop Solution</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Everything you need to keep your website running at peak performance and responsive 
                to your evolving needs—all in one place at one price.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {solutions.map((solution, index) => (
                <div
                  key={index}
                  className="bg-secondary/50 border border-border ring-1 ring-white/[0.08] dark:ring-white/[0.12] rounded-lg p-5"
                >
                  <p className="text-sm text-muted-foreground">{solution}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Plans */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Choose Your Plan</h2>
              <p className="text-muted-foreground">
                Select the plan that fits your needs. All plans include hosting, domain, and support. 
                Upgrade or downgrade at anytime.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative bg-secondary/50 border rounded-lg p-6 ${
                    plan.popular ? "border-primary" : "border-border"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-medium">
                      Most Popular
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-bold mb-2">{plan.name}</h3>
                    <div className="text-3xl font-bold">
                      {plan.price}
                      <span className="text-sm font-normal text-muted-foreground">{plan.period}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{plan.annual}</p>
                    <p className="text-sm text-muted-foreground mt-3">{plan.description}</p>
                  </div>
                  
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        {feature.included ? (
                          <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        ) : (
                          <Minus className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        )}
                        <span className={feature.included ? "" : "text-muted-foreground"}>
                          {feature.name}
                          {typeof feature.included === "string" && (
                            <span className="text-muted-foreground ml-1">({feature.included})</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button asChild variant={plan.variant} className="w-full">
                    <Link to={`/quote?plan=${plan.name.toLowerCase()}`}>{plan.cta}</Link>
                  </Button>
                </div>
              ))}
            </div>
            
            <p className="text-center text-sm text-muted-foreground mt-8">
              Custom and higher-tier enterprise plans are also available. Contact us to discuss.
            </p>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-secondary/50 border border-border ring-1 ring-white/[0.08] dark:ring-white/[0.12] rounded-lg px-6 data-[state=open]:bg-secondary/70"
                >
                  <AccordionTrigger className="text-left text-base md:text-lg font-medium hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 border-t border-border">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-muted-foreground mb-8">
                Choose your plan and let us handle the technical details while you focus on growing your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="/meeting">
                    SCHEDULE A CALL
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <a href="/contact">CONTACT US</a>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Maintenance;
